import { useTranslation } from "react-i18next";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";

/** A choice the graph view holds for itself (F76, F59), by its words under `workflows.<kind>`. */
export type GraphViewChoiceKind = "density" | "layout";

/**
 * One of the graph view's own choices, such as how much the cards show or which way the steps
 * run, as a row of buttons; in a narrow view, a select of the same choices, so the controls take
 * one line above the graph (T1-5). The choice belongs to the view it sits on; Personal preferences
 * hold the default for a view not yet set (F76, F59).
 */
export default function GraphViewChoice<Choice extends string>({ kind, choices, value, onChange }: { kind: GraphViewChoiceKind; choices: readonly Choice[]; value: Choice; onChange: (choice: Choice) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="app-text-muted text-xs" aria-hidden="true">{t(`workflows.${kind}.short`)}</span>
      <div className="app-view-choice-buttons app-page-switcher app-flow-display-tabs flex" role="group" aria-label={t(`workflows.${kind}.label`)}>
        {choices.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            title={t(`workflows.${kind}.help.${option}`)}
            className="app-page-switcher-button px-2.5 py-1 text-xs"
            onClick={() => {
              onChange(option);
            }}
          >
            {t(`workflows.${kind}.options.${option}`)}
          </button>
        ))}
      </div>
      <select
        className={`app-view-choice-select ${FORM_SELECT_CLASS} w-auto py-0.5 text-xs`}
        aria-label={t(`workflows.${kind}.label`)}
        value={value}
        onChange={(event) => {
          const chosen = choices.find((option) => option === event.target.value);
          if (chosen !== undefined) onChange(chosen);
        }}
      >
        {choices.map((option) => (
          <option key={option} value={option}>
            {t(`workflows.${kind}.options.${option}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
